/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import {
	type ComponentPropsWithRef,
	forwardRef,
	type ForwardRefExoticComponent,
	type RefAttributes,
} from 'react';

import { cssMap, jsx } from '@atlaskit/css';
import { Pressable, Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

const styles = cssMap({
	button: {
		display: 'contents',
	},
	innerContainer: {
		display: 'inline',
		backgroundClip: 'padding-box',
		boxDecorationBreak: 'clone',
		fontWeight: token('font.weight.medium'),
		paddingLeft: token('space.075'),
		paddingTop: token('space.025'),
		paddingBottom: token('space.025'),
		paddingRight: token('space.075'),
		textAlign: 'initial',
		whiteSpace: 'break-spaces',
		wordBreak: 'break-all',
	},
	innerContainerSocialProofConnect: {
		color: token('color.text.inverse'),
		cursor: 'pointer',
		backgroundColor: token('color.background.selected.bold'),
		'&:hover': {
			backgroundColor: token('color.background.selected.bold.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.selected.bold.pressed'),
		},
		borderRadius: token('radius.small'),
		marginRight: token('space.025'),
		display: 'inline',
		alignItems: 'center',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
		fontSize: '0.8em' as any,
		backgroundClip: 'padding-box',
		boxDecorationBreak: 'clone',
		paddingLeft: token('space.075'),
		paddingTop: token('space.025'),
		paddingBottom: token('space.025'),
		paddingRight: token('space.075'),
		textAlign: 'initial',
		verticalAlign: '1px',
		whiteSpace: 'break-spaces',
		wordBreak: 'break-all',
	},
	innerContainerNoWrap: {
		whiteSpace: 'nowrap',
		wordBreak: 'normal',
	},
	enabled: {
		color: token('color.text'),
		cursor: 'pointer',
		backgroundColor: token('color.background.neutral'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.pressed'),
		},
	},
	disabled: {
		color: token('color.text.disabled'),
		cursor: 'not-allowed',
		backgroundColor: token('color.background.disabled'),
	},
	unauthorised: {
		color: token('color.text.inverse'),
		cursor: 'pointer',
		backgroundColor: token('color.background.selected.bold'),
		'&:hover': {
			backgroundColor: token('color.background.selected.bold.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.selected.bold.pressed'),
		},
		borderTopRightRadius: token('radius.xsmall'),
		borderBottomRightRadius: token('radius.xsmall'),
		borderTopLeftRadius: token('radius.xsmall'),
		borderBottomLeftRadius: token('radius.xsmall'),
		// The following style rules are ignored by https://atlassian.slack.com/archives/C09A7MGD4R2/p1762172701565049?thread_ts=1761943025.332209&cid=C09A7MGD4R2
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
		paddingTop: '1px' as any,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
		paddingBottom: '1px' as any,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
		marginRight: '1px' as any,
		fontWeight: token('font.weight.regular'),
	},
});

/* eslint-enable @compiled/shorthand-property-sorting */
type ActionButtonProps = ComponentPropsWithRef<typeof Pressable> & {
	isSlimDesign?: boolean;
	viewType?: 'default' | 'unauthorised' | 'action';
};

/**
 * Action button has to be a span for the overflow to work correctly
 */
export const ActionButton: ForwardRefExoticComponent<
	Omit<ActionButtonProps, 'ref'> & RefAttributes<HTMLButtonElement>
> = forwardRef(
	(
		{
			children,
			isDisabled,
			viewType = 'default',
			isSlimDesign = false,
			...props
		}: ActionButtonProps,
		ref: ActionButtonProps['ref'],
	) => {
		const shouldUseUnauthorisedSlimStyle = viewType === 'unauthorised' && isSlimDesign;

		if (shouldUseUnauthorisedSlimStyle && !isDisabled) {
			const { onClick, ...boxProps } = props;
			return (
				<Box {...boxProps} ref={ref} style={{ font: `inherit` }} xcss={styles.button}>
					<span css={[styles.innerContainerSocialProofConnect, styles.innerContainerNoWrap]}>
						{children}
					</span>
				</Box>
			);
		}

		return (
			<Pressable
				{...props}
				isDisabled={isDisabled}
				ref={ref}
				// We need to reset Pressable fixed font size to allow inline card to inherit the font size
				// from parent component such as paragraph and heading
				style={{ font: `inherit` }}
				xcss={styles.button}
			>
				<span
					css={[
						styles.innerContainer,
						isDisabled
							? styles.disabled
							: viewType === 'unauthorised'
								? styles.unauthorised
								: styles.enabled,
					]}
					tabIndex={isDisabled ? undefined : 0}
				>
					{children}
				</span>
			</Pressable>
		);
	},
);
